import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { TbEdit } from 'react-icons/tb';
import { FaCheck } from 'react-icons/fa';
import { useTranslation } from '@/hooks/useTranslation';
import TooltipFFXIV from '@/app/components/TooltipFFXIV';
import TextFFXIV from '@/app/components/TextFFXIV';
import IconGlowWrapper from '@/app/components/IconGlowWrapper';
import BarFFXIV from '@/app/components/BarFFXIV';
import PlayerAvatar, { calculateDuplicateNumbers } from '@/app/components/PlayerAvatar';
import GuessingIndicator from './GuessingIndicator';
import { Player } from '@/lib/types/common/Player';
import { GameStep } from '@/lib/types/common/GameStep';
import { useGame } from '@/app/providers/GameContextProvider';
import { useGlobalPopup } from '@/app/providers/GlobalPopupProvider';
import { AUDIO, useAudio } from '@/app/providers/AudioProvider';

export interface PlayerRowProps {
    sessionId: string;
    player: Player;
    isCurrentPlayer: boolean;
    isInLobby: boolean;
    gameStep: GameStep;
    gameMasterId: string;
    currentRound: number;
    currentHazards: string[];
    onAvatarClick: () => void;
    // Optional: Score bar display (for non-team mode)
    showScoreBar?: boolean;
    totalScore?: number;
    currentStreak?: number;
}

const PlayerRow: React.FC<PlayerRowProps> = ({
    sessionId,
    player,
    isCurrentPlayer,
    isInLobby,
    gameStep,
    gameMasterId,
    currentRound,
    currentHazards,
    onAvatarClick,
    showScoreBar = false,
    totalScore = 0,
    currentStreak = 0,
}) => {
    const { gameState, players, room } = useGame();
    const { t } = useTranslation();
    const { showPopup } = useGlobalPopup();
    const [isEditing, setIsEditing] = useState(false);
    const [inputName, setInputName] = useState("");
    const { play } = useAudio();
    const shouldShowLobbyInfo = isInLobby;

    // Calculate duplicate numbers for all players
    // Create a stable key from all player avatars to ensure proper reactivity when avatars change
    const playersAvatarKey = gameState?.players
        ? Array.from(gameState.players.entries()).map(([sid, p]) => `${sid}:${p.avatarId}`).join(',')
        : '';
    const duplicateNumbers = useMemo(() => {
        if (!gameState?.players) return new Map<string, number>();
        const playerList = Array.from(gameState.players.entries()).map(([sid, p]) => ({ sessionId: sid, avatarId: p.avatarId }));
        return calculateDuplicateNumbers(playerList);
    }, [playersAvatarKey, gameState?.players]);
    const duplicateNumber = duplicateNumbers.get(sessionId) || 0;

    const handleEditName = () => {
        setIsEditing(true);
        setInputName("");
    };

    const handleUpdateName = async (newName: string) => {
        setIsEditing(false);
        const trimmedName = newName.trim();
        if (!room) return;
        if (trimmedName === "") return;
        if (trimmedName.length > 30) {
            showPopup({ type: 'error', message: t("lobby.nameTooLong") });
            return;
        }
        try {
            room.send("updatePlayerName", { name: trimmedName });
            localStorage.setItem('playerName', trimmedName);
            showPopup({ type: 'success', message: t("lobby.nameUpdated") });
        } catch {
            showPopup({ type: 'error', message: t("lobby.failedToUpdateName") });
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleUpdateName(inputName);
        }
        if (e.key === 'Escape') {
            setIsEditing(false);
        }
    };

    const handleInputBlur = () => {
        handleUpdateName(inputName);
    };

    const isRecapNotInLobby = gameStep === GameStep.RECAP && !player.inLobby;
    const shouldShowScoreBar = showScoreBar && !gameState?.isEffectivelyEndless && gameStep !== GameStep.LOBBY && !isInLobby;
    const shouldShowStreak = showScoreBar && gameState?.isEffectivelyEndless && gameStep !== GameStep.LOBBY && !isInLobby;

    // Calculate score percentage for the bar
    const getScorePercentage = () => {
        const denominator = 1000 * Math.max(0, currentRound - 1);
        return denominator === 0 ? 100 : Math.min((totalScore / denominator) * 100, 100);
    };

    const scorePercentage = getScorePercentage();
    const isPerfectScore = scorePercentage === 100 && totalScore > 0;

    const myTeam = gameState?.teams.find(team => team.id === player.teamId);
    const myTeamMembers = players.filter(p => myTeam?.id === p.teamId);
    const hasGuessed = gameState?.isEffectivelyCoop ? myTeamMembers.some(member => member.hasGuessed) : player.hasGuessed;

    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className={`relative w-full ${isRecapNotInLobby ? 'opacity-50 group' : ''} ${player.isEliminated && gameStep !== GameStep.LOBBY && gameStep !== GameStep.RECAP ? 'grayscale opacity-50' : ''}`}
        >
            {isCurrentPlayer && (
                <div
                    className="absolute z-[-1] top-1/2 left-1/2 translate-y-[-50%] translate-x-[-50%] w-[calc(100%+40px)] h-[140%] opacity-50 bg-gradient-to-b from-[#BF9474]/0 via-[#CDA042]/70 via-[50%] to-[#BF9474]/0 blur-[1px]"
                    style={{
                        maskImage: 'linear-gradient(to right, transparent 0%, black 20%, black 80%, transparent 100%)'
                    }}>
                </div>
            )}

            {isRecapNotInLobby && (
                <TooltipFFXIV>
                    {t("lobby.playerViewingRecap")}
                </TooltipFFXIV>
            )}
            <div>
                <div className="flex gap-2 items-center">
                    {/* Avatar */}
                    <div
                        className={`relative ${isCurrentPlayer && shouldShowLobbyInfo ? 'cursor-pointer' : ''}`}
                        onMouseEnter={() => player.inLobby && isCurrentPlayer ? play(AUDIO.HOVER) : null}
                        onClick={() => isCurrentPlayer && shouldShowLobbyInfo && onAvatarClick()}
                    >
                        <PlayerAvatar
                            avatarId={player.avatarId}
                            duplicateNumber={duplicateNumber}
                            size={40}
                            className="w-7 h-7 xl:w-10 xl:h-10"
                            imageClassName={`transition-transform duration-200 ${isCurrentPlayer && gameStep === GameStep.LOBBY ? 'hover:scale-110' : ''}`}
                            isLeader={gameMasterId === sessionId}
                            leaderIconSize={24}
                        />
                    </div>

                    {/* Name and edit controls */}
                    <div className="flex flex-col grow">
                        <div className="flex items-center gap-2">
                            {isCurrentPlayer && isEditing ? (
                                <>
                                    <input
                                        className="ml-1 px-2 max-w-40 bg-transparent border-[1px] border-white h-7 shadow-[0_0_10px_1px_#2563eb,0_0_10px_1px_#2563eb_inset] rounded-md font-myriad text-sm font-bold outline-none cursor-text"
                                        value={inputName}
                                        onChange={e => setInputName(e.target.value)}
                                        onKeyDown={handleInputKeyDown}
                                        onBlur={handleInputBlur}
                                        autoFocus
                                        placeholder={t("lobby.enterYourName")}
                                        style={{ transform: 'rotateX(20deg)' }}
                                    />
                                    <div className="group absolute right-0 translate-x-full flex items-center">
                                        <IconGlowWrapper
                                            icon={<FaCheck />}
                                            onClick={() => handleUpdateName(inputName)}
                                            className="text-xl ml-3"
                                        />
                                        <TooltipFFXIV>
                                            <TextFFXIV text={t("lobby.confirmNameChange")} />
                                        </TooltipFFXIV>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="flex items-center">
                                        <div className="text-shadow shadow-blue-800 font-myriad text-sm xl:text-xl whitespace-nowrap truncate max-w-[110px] xl:max-w-none" style={{ transform: 'rotateX(20deg)' }}>{player.name}</div>
                                        {isCurrentPlayer && shouldShowLobbyInfo && (
                                            <div className="group absolute right-0 translate-x-full flex items-center">
                                                <IconGlowWrapper
                                                    icon={<TbEdit />}
                                                    onClick={handleEditName}
                                                    className="text-base xl:text-xl xl:ml-3"
                                                />
                                                <TooltipFFXIV>
                                                    <TextFFXIV text={t("lobby.editYourName")} />
                                                </TooltipFFXIV>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Score Bar */}
                        {shouldShowScoreBar && (
                            <BarFFXIV
                                percent={scorePercentage}
                                number={totalScore}
                                golden={isPerfectScore}
                            />
                        )}

                        {/* Endless Streak */}
                        {shouldShowStreak && (
                            <div className="absolute -bottom-2 right-0 text-sm font-bold text-shadow shadow-blue-900 uppercase">
                                {t("play.streak")} {currentStreak}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Guessing indicator */}
            {gameStep === GameStep.GUESSING && (
                <GuessingIndicator
                    hasGuessed={hasGuessed}
                    isEliminated={player.isEliminated}
                    hasHazardsActive={gameState?.hasHazardsActive ?? false}
                    currentHazards={currentHazards}
                />
            )}
        </motion.div>
    );
};

export default PlayerRow;
